import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { profileApi } from '@/lib/api/profile';
import { onProfileMutationSuccess } from '@/lib/profile-query';
import {
  ProfileDto,
  UpdateProfileDto,
  ConsultantCredentialsStatus,
  ensureHttpsUrl,
} from '@paladin-monorepo/common';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Globe } from 'lucide-react';
import { LinkedinIcon } from '@/components/icons/LinkedinIcon';

const BIO_RECOMMENDED_CHARS = 300;

interface PersonalInfoFormValues extends UpdateProfileDto {}

export const PersonalInfoCard = () => {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      yup.object({
        firstName: yup.string().max(255),
        lastName: yup.string().max(255),
        description: yup.string(),
        jobTitle: yup.string().max(255),
        linkedinUrl: yup
          .string()
          .nullable()
          .max(2048)
          .transform((v) => {
            if (v === '' || v === undefined || v === null) return null;
            return ensureHttpsUrl(String(v)) || null;
          })
          .test(
            'url',
            t('profile.personalInfo.links.invalidUrl'),
            (v) => !v || /^https?:\/\/.+/i.test(v),
          ),
        portfolioUrl: yup
          .string()
          .nullable()
          .max(2048)
          .transform((v) => {
            if (v === '' || v === undefined || v === null) return null;
            return ensureHttpsUrl(String(v)) || null;
          })
          .test(
            'url',
            t('profile.personalInfo.links.invalidUrl'),
            (v) => !v || /^https?:\/\/.+/i.test(v),
          ),
      }),
    [t],
  );

  const { data: profile, isLoading } = useQuery<ProfileDto>({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
  });

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: UpdateProfileDto) => profileApi.updateProfile(data),
    onSuccess: (data) => {
      onProfileMutationSuccess(queryClient, data);
      toast.success(t('profile.personalInfo.update.success'));
    },
    onError: () => {
      toast.error(t('profile.personalInfo.update.error'));
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => profileApi.uploadPhoto(file),
    onSuccess: (data) => {
      onProfileMutationSuccess(queryClient, data);
    },
    onError: () => {
      toast.error(t('profile.personalInfo.photo.uploadError'));
    },
  });

  const profilePhoto = profileApi.getMyPhotoUrl();

  const [isEditing, setIsEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setSelectedFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }

    const maxBytes = 5 * 1024 * 1024; // 5MB
    if (file.size > maxBytes) {
      toast.error(t('profile.personalInfo.photo.tooLarge', { maxSize: 5 }));
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.personalInfo.photo.invalidType'));
      return;
    }

    setSelectedFile(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<PersonalInfoFormValues>({
    resolver: yupResolver<
      PersonalInfoFormValues,
      unknown,
      PersonalInfoFormValues
    >(schema),
    defaultValues: {
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      description: profile?.description || '',
      jobTitle: profile?.jobTitle || '',
      linkedinUrl: profile?.linkedinUrl ?? '',
      portfolioUrl: profile?.portfolioUrl ?? '',
    },
  });

  // Update form defaults when profile loads
  useEffect(() => {
    if (profile) {
      reset({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        description: profile.description || '',
        jobTitle: profile.jobTitle || '',
        linkedinUrl: profile.linkedinUrl ?? '',
        portfolioUrl: profile.portfolioUrl ?? '',
      });
    }
  }, [profile, reset]);

  const descriptionLen = watch('description')?.length ?? 0;

  const onSubmit = async (data: PersonalInfoFormValues) => {
    try {
      if (selectedFile) {
        await uploadMutation.mutateAsync(selectedFile);
        clearSelectedFile();
      }
      const payload: UpdateProfileDto = {
        ...data,
        linkedinUrl: data.linkedinUrl?.trim() || null,
        portfolioUrl: data.portfolioUrl?.trim() || null,
      };
      await mutation.mutateAsync(payload);
      setIsEditing(false);
    } catch {
      // errors are handled in mutations' onError
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.personalInfo.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div>{t('generic.loading')}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('profile.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {!isEditing ? (
          <div className="flex flex-col items-center text-center space-y-4">
            {profilePhoto && (
              <img
                src={profilePhoto}
                alt={t('profile.title') as string}
                className="h-40 w-40 rounded-full object-cover border"
              />
            )}

            <div className="space-y-1">
              <h2 className="text-xl font-semibold">
                {[profile?.firstName, profile?.lastName]
                  .filter(Boolean)
                  .join(' ') || '-'}
              </h2>
              {profile?.jobTitle && (
                <p className="text-sm text-muted-foreground">
                  {profile.jobTitle}
                </p>
              )}
            </div>

            <Badge variant="secondary">
              {t(
                `profile.personalInfo.consultantCredentials.status.${profile?.consultantCredentialsStatus ?? ConsultantCredentialsStatus.None}`,
              )}
            </Badge>

            {(profile?.linkedinUrl || profile?.portfolioUrl) && (
              <div className="flex items-center gap-3">
                {profile?.linkedinUrl && (
                  <a
                    href={profile.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('profile.personalInfo.linkedin.label') as string}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <LinkedinIcon className="h-5 w-5" />
                  </a>
                )}
                {profile?.portfolioUrl && (
                  <a
                    href={profile.portfolioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('profile.personalInfo.portfolio.label') as string}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Globe className="h-5 w-5" />
                  </a>
                )}
              </div>
            )}

            {profile?.description && (
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {profile.description}
              </p>
            )}

            <Button type="button" onClick={() => setIsEditing(true)}>
              {t('profile.personalInfo.edit')}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="flex flex-col items-center">
              {/* <Label htmlFor="photo">{t('profile.personalInfo.photo.label')}</Label> */}
              <div className="flex items-center gap-4">
                <div className="h-40 w-40 rounded-full overflow-hidden border bg-muted">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={t('profile.personalInfo.photo.previewAlt') as string}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    (() => {
                      return profilePhoto ? (
                        <img
                          src={profilePhoto}
                          alt={
                            t('profile.personalInfo.photo.currentAlt') as string
                          }
                          className="h-full w-full object-cover"
                        />
                      ) : null;
                    })()
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Input
                    id="photo"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {selectedFile && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{selectedFile.name}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearSelectedFile}
                      >
                        {t('generic.remove')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="firstName">
                  {t('profile.personalInfo.firstName.label')}
                </Label>
                <Input id="firstName" {...register('firstName')} />
                {errors.firstName && (
                  <p className="text-sm text-red-500">
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="lastName">
                  {t('profile.personalInfo.lastName.label')}
                </Label>
                <Input id="lastName" {...register('lastName')} />
                {errors.lastName && (
                  <p className="text-sm text-red-500">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="jobTitle">
                {t('profile.personalInfo.jobTitle.label')}
              </Label>
              <Input id="jobTitle" {...register('jobTitle')} />
              {errors.jobTitle && (
                <p className="text-sm text-red-500">
                  {errors.jobTitle.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="linkedinUrl">
                {t('profile.personalInfo.linkedin.label')}
              </Label>
              <Input
                id="linkedinUrl"
                type="text"
                inputMode="url"
                placeholder="https://"
                {...register('linkedinUrl')}
              />
              {errors.linkedinUrl && (
                <p className="text-sm text-red-500">
                  {errors.linkedinUrl.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="portfolioUrl">
                {t('profile.personalInfo.portfolio.label')}
              </Label>
              <Input
                id="portfolioUrl"
                type="text"
                inputMode="url"
                placeholder="https://"
                {...register('portfolioUrl')}
              />
              {errors.portfolioUrl && (
                <p className="text-sm text-red-500">
                  {errors.portfolioUrl.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="description">
                {t('profile.personalInfo.description.label')}
              </Label>
              <Textarea id="description" rows={6} {...register('description')} />
              <p className="text-xs text-muted-foreground">
                {t('profile.personalInfo.description.charCount', {
                  count: descriptionLen,
                  target: BIO_RECOMMENDED_CHARS,
                })}
              </p>
              {errors.description && (
                <p className="text-sm text-red-500">
                  {errors.description.message}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={mutation.isPending || uploadMutation.isPending}
              >
                {t('profile.personalInfo.save')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  clearSelectedFile();
                  setIsEditing(false);
                }}
              >
                {t('profile.personalInfo.cancel')}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};
